import { Box, Button, Collapse, IconButton, Paper, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { IEmployeeSuggestedCompetence } from '@sharedInterfaces/IEmployee';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import getSuggestedCompetences from '@src/APIs/graphQl/Employee/Suggestions/getSuggestedCompetences';
import FormatedTextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/FormatedTextInput/FormatedTextInput';
import type { AppState } from '@store/store';
import { ChevronDown, ChevronUp, Lightbulb, Plus } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

function SuggestedCompetencesWidget() {
    const theme = useTheme();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).competence;
    const offline = useSelector((state: AppState) => state.client.offline);

    const [competences, setCompetences] = useState<IEmployeeSuggestedCompetence[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

    useEffect(() => {
        getSuggestedCompetences((suggestedCompetences) => {
            setCompetences(suggestedCompetences.slice(0, 3)); // Show only top 3
            setLoading(false);
        });
    }, []);

    const toggleExpanded = (competenceId: number) => {
        setExpandedItems((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(competenceId)) {
                newSet.delete(competenceId);
            } else {
                newSet.add(competenceId);
            }
            return newSet;
        });
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                bgcolor: 'background.paper',
            }}
        >
            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Vorgeschlagene Kompetenzen
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Diese Kompetenzen haben Sie nach unserer Analyse schon. Fügen Sie diese gern zu ihrem Profil hinzu.
                </Typography>
            </Box>

            {loading ? (
                <Typography variant="body2" color="text.secondary">
                    Lade Vorschläge...
                </Typography>
            ) : competences.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Lightbulb size={32} style={{ color: theme.palette.text.secondary, marginBottom: '8px' }} />
                    <Typography variant="body2" color="text.secondary">
                        Keine Vorschläge verfügbar
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {competences.map((competence) => {
                        const isExpanded = expandedItems.has(competence.id);
                        return (
                            <Box
                                key={competence.id}
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        mb: 1,
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 600,
                                            color: theme.palette.primary.main,
                                            flex: 1,
                                            fontSize: '0.875rem',
                                        }}
                                    >
                                        {competence.title}
                                    </Typography>
                                    {competence.description && (
                                        <IconButton
                                            size="small"
                                            onClick={() => toggleExpanded(competence.id)}
                                            sx={{ ml: 1, p: 0.5 }}
                                        >
                                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                        </IconButton>
                                    )}
                                </Box>

                                {competence.description && (
                                    <Box sx={{ mb: 1 }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                fontSize: '0.875rem',
                                                lineHeight: 1.4,
                                                ...(!isExpanded && {
                                                    overflow: 'hidden',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 3,
                                                    WebkitBoxOrient: 'vertical',
                                                }),
                                            }}
                                            dangerouslySetInnerHTML={{ __html: competence.description }}
                                        />
                                    </Box>
                                )}

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        size="small"
                                        startIcon={<Plus size={14} />}
                                        sx={{
                                            textTransform: 'none',
                                            fontSize: '0.75rem',
                                            minHeight: 'auto',
                                            py: 0.5,
                                        }}
                                    >
                                        Hinzufügen
                                    </Button>
                                </Box>
                            </Box>
                        );
                    })}

                    {competences.length > 0 && (
                        <Button
                            variant="text"
                            size="small"
                            sx={{
                                textTransform: 'none',
                                mt: 1,
                                alignSelf: 'center',
                            }}
                        >
                            Alle Vorschläge anzeigen
                        </Button>
                    )}
                </Box>
            )}
        </Paper>
    );
}

export default SuggestedCompetencesWidget;
